/** Shared utilities for loan data fetching across hooks. */
import { supabase } from '@/integrations/supabase/client';

/** Statuses considered "active" for loan display purposes: ACTIVE, RETURN_PENDING, PENDING_ACCEPTANCE. */
export const ACTIVE_LOAN_STATUSES = ['ACTIVE', 'RETURN_PENDING', 'PENDING_ACCEPTANCE'];

/** Shape of a loan record returned by {@link fetchLoansWithFallback}. */
export interface LoanRecord {
  id: string;
  item_id: string;
  borrower_id: string;
  lender_user_id?: string;
  borrowed_at: string;
  due_at: string | null;
  status: string;
  return_initiated_by?: string | null;
  group_id?: string | null;
  notes?: string | null;
}

// [LEARN] Priority-based deduplication: when an item has multiple loans, we sort by this map
// (lower number = higher priority) and keep only the first loan per item_id.
const LOAN_PRIORITY: Record<string, number> = {
  ACTIVE: 0,
  RETURN_PENDING: 1,
  PENDING_ACCEPTANCE: 2,
};

/**
 * Fetches loans for a set of item IDs with automatic fallback on schema mismatch.
 *
 * If the primary query (full columns, all active statuses) fails — e.g. because a
 * column or enum value is not yet in the DB after a migration — retries with
 * base-schema columns and ACTIVE status only. This ensures loans are visible even
 * when newer migrations have not been applied.
 *
 * @param itemIds - Item IDs to fetch loans for.
 * @param filter - Optional additional equality filter as `[columnName, value]`,
 *   e.g. `['borrower_id', userId]`.
 * @returns Array of {@link LoanRecord} objects (empty array on total failure).
 */
export async function fetchLoansWithFallback(
  itemIds: string[],
  filter?: [string, string],
): Promise<LoanRecord[]> {
  const fullColumns =
    'id, item_id, borrower_id, lender_user_id, borrowed_at, due_at, status, return_initiated_by, group_id, notes';
  const baseColumns = 'id, item_id, borrower_id, lender_user_id, borrowed_at, due_at, status';

  let query = supabase
    .from('loans')
    .select(fullColumns)
    .in('item_id', itemIds)
    .in('status', ACTIVE_LOAN_STATUSES);

  if (filter) {
    query = query.eq(filter[0], filter[1]);
  }

  const { data: loans, error: loansError } = await query;

  if (!loansError) return (loans ?? []) as LoanRecord[];

  if (import.meta.env.DEV) {
    console.warn(
      '[fetchLoansWithFallback] Primary query failed, retrying with base columns:',
      loansError.message,
    );
  }

  let fallbackQuery = supabase
    .from('loans')
    .select(baseColumns)
    .in('item_id', itemIds)
    .in('status', ['ACTIVE']);

  if (filter) {
    fallbackQuery = fallbackQuery.eq(filter[0], filter[1]);
  }

  const { data: fallbackLoans, error: fallbackError } = await fallbackQuery;

  if (fallbackError) {
    if (import.meta.env.DEV) {
      console.error('[fetchLoansWithFallback] Fallback query also failed:', fallbackError.message);
    }
    return [];
  }

  return (fallbackLoans ?? []) as LoanRecord[];
}

/**
 * Deduplicates an array of loans, returning a Map of `item_id` to the
 * highest-priority loan per item.
 *
 * Priority order: ACTIVE > RETURN_PENDING > PENDING_ACCEPTANCE. Only the
 * single highest-priority loan per item is kept.
 *
 * @param loans - Array of loan-like objects containing at least `item_id` and `status`.
 * @returns Map from `item_id` to the winning loan record.
 */
// Generic: works with any object that has item_id and status
export function buildLoansByItemMap<T extends { item_id: string; status: string }>(
  loans: T[],
): Map<string, T> {
  const sorted = [...loans].sort(
    (a, b) => (LOAN_PRIORITY[a.status ?? ''] ?? 9) - (LOAN_PRIORITY[b.status ?? ''] ?? 9),
  );
  const map = new Map<string, T>();
  for (const loan of sorted) {
    if (!map.has(loan.item_id)) map.set(loan.item_id, loan);
  }
  return map;
}

/**
 * Creates a synthetic active-loan stub for items that report ON_LOAN status
 * but have no matching loan record (e.g. when the loans query failed silently
 * or the loan row was removed).
 *
 * @param item - Object with at least `status`, `current_holder_user_id`, and `updated_at`.
 * @returns A minimal loan stub if the item is ON_LOAN with a known holder, otherwise `null`.
 */
export function synthesiseLoanStub(item: {
  status: string;
  current_holder_user_id: string | null;
  updated_at: string;
}) {
  if (item.status === 'ON_LOAN' && item.current_holder_user_id) {
    return {
      id: 'unknown',
      borrower_id: item.current_holder_user_id,
      borrowed_at: item.updated_at,
      borrower_name: null,
      due_at: null,
      status: 'ACTIVE' as string | null,
      return_initiated_by: null,
      group_id: null,
      notes: null,
    };
  }
  return null;
}
